import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Observable,
  defaultDataIdFromObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { message, notification } from 'antd';
import { createUploadLink } from 'apollo-upload-client';
import config from 'config';
import { createDeferredDemoLink } from 'demo/deferredLink';
import TokenRefresher from 'helpers/tokenRefresher';
import { isDemo } from 'helpers/utility';
import translate from 'languageProvider/inline';
import actions from 'redux/auth/actions';
import maintenanceActions from 'redux/maintenance/actions';
import storeObject from 'redux/storeDeclaration';

//https://github.com/graphql/graphql-js/pull/3501
globalThis.process ??= { env: import.meta.env };

const cache = new InMemoryCache({
  dataIdFromObject: (object) => {
    switch (object.__typename) {
      case 'CVDatabaseSearchResultExtendedDataItemValue':
        return `${object.__typename}:${object.id}-${object.label}:${object.secondaryId}-${object.secondaryLabel}:${object.dateTime}`;
      default:
        return defaultDataIdFromObject(object);
    }
  },
  typePolicies: {
    CompanyProfile: {
      fields: {
        companyBenefits: {
          merge(existing = [], incoming) {
            if (incoming) {
              return incoming;
            }
            return existing;
          },
        },
      },
    },
  },
});

const httpLink = createUploadLink({ uri: config.graphqlUrl });
const checkErrorLink = new ApolloLink(
  (operation, forward) => forward(operation).map((response) => response) // eslint-disable-line
);

const serverErrorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (networkError && [401, 403].includes(networkError.statusCode)) {
    if (
      networkError.statusCode === 403 &&
      networkError.result &&
      networkError.result.message &&
      networkError.result.message === 'This token has expired'
    ) {
      /* expired token, try with refresh token and repeat request */
      return new Observable((observer) => {
        if (!TokenRefresher.getRefreshing()) {
          TokenRefresher.refresh();
        }

        TokenRefresher.subscribeTokenRefresh((success, error) => {
          if (success) {
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };

            // Retry last failed request
            forward(operation).subscribe(subscriber);
          } else {
            // No refresh or client token available, we force user to login
            observer.error(error);
            storeObject.getStore().dispatch(
              actions.logout({
                remember: true,
                requestLogin: true,
              })
            );
          }
        });
      });
    }
    storeObject.getStore().dispatch(
      actions.logout({
        remember: true,
        requestLogin: true,
      })
    );
  }

  if (networkError && networkError.statusCode === 503) {
    storeObject.getStore().dispatch(maintenanceActions.toggleMaintenance(true));
    return null;
  }

  if (graphQLErrors && graphQLErrors.length) {
    const messages = [];
    graphQLErrors.forEach((error) => {
      const { message: errorMessage } = error;
      if (errorMessage.indexOf('showError: ') !== -1) {
        const items = JSON.parse(errorMessage.replace('showError: ', ''));
        items.forEach((item) => {
          messages.push(item);
        });
      }
    });

    if (messages.length) {
      messages.forEach((item) => {
        message.error(item);
      });
    }
  }
  if (
    networkError &&
    (networkError.toString() === 'TypeError: Failed to fetch' ||
      networkError.toString() === 'TypeError: Network request failed')
  ) {
    notification.error({
      title: translate('error.networkError'),
      description: translate('error.lostConnection'),
    });
  }

  return null;
});

const authLink = setContext((_, { headers }) => ({
  // return the headers to the context so httpLink can read them
  headers: {
    ...headers,
    locale: storeObject.getStore().getState().LanguageSwitcher.get('language').locale,
    ClientVersion: import.meta.env.VITE_APP_CLIENT_VERSION,
  },
  credentials: 'include',
}));

let link;

if (isDemo()) {
  link = createDeferredDemoLink();
} else {
  link = authLink.concat(serverErrorLink.concat(checkErrorLink.concat(httpLink)));
}

const client = new ApolloClient({
  link,
  cache,
});

export default client;
